/**
 * Unicode-safe token normalization shared by the index and the resolver.
 *
 * Both sides of a comparison go through the same function, so a citation and a reference
 * agree on the form of a name even when one was typed with a precomposed character and the
 * other with a combining mark. Non-Latin scripts are kept: `\p{L}` and `\p{N}` are the only
 * characters retained, never an ASCII range, so a Cyrillic or Greek surname does not collapse
 * to an empty string that every other such surname would also collapse to.
 */

/** Lower-case words that sit before a surname in prose and are often stored outside `family`. */
export const PARTICLE_WORDS: ReadonlySet<string> = new Set([
  'van', 'von', 'der', 'den', 'de', 'du', 'des', 'la', 'le', 'di', 'da', 'dos', 'das', 'del',
  'della', 'ten', 'ter', 'te', 'zu', 'zum', 'zur', 'vom', 'op', 'af', 'av', 'al', 'el', 'bin', 'ibn',
]);

/** NFKD, strip combining marks, lower-case, keep letters and digits only. */
export function normalizeToken(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '');
}

/** Split on whitespace and punctuation, keeping apostrophes and hyphens inside a word. */
export function words(s: string): string[] {
  return s.split(/[^\p{L}\p{M}\p{N}'’\u2010\u2011-]+/u).filter((w) => w.length > 0 && /[\p{L}\p{N}]/u.test(w));
}

/** The normalized, concatenated form of a run of words: `["Van", "der", "Meer"]` → `vandermeer`. */
export function joined(ws: string[]): string {
  return ws.map(normalizeToken).join('');
}

/** Drop leading particle words, always leaving at least one word. */
export function stripParticles(ws: string[]): string[] {
  let i = 0;
  while (i < ws.length - 1 && PARTICLE_WORDS.has(ws[i].toLowerCase())) i++;
  return ws.slice(i);
}

/** `Aguirre-Loaiza` → `["Aguirre", "Loaiza"]` (also across U+2010 and U+2011); a word without
 *  a hyphen → `[]`. */
export function hyphenParts(w: string): string[] {
  if (!/[\u2010\u2011-]/u.test(w)) return [];
  return w.split(/[\u2010\u2011-]/u).filter((p) => p.length > 0);
}

/** The English possessive clitic on a written surname: `Cohen's`, `Cohen’s`, `Jones'`. */
export const POSSESSIVE_RE = /['’]s?$/u;
